import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  IconButton,
  Card,
  CardContent,
  InputAdornment,
  Fade,
  useTheme,
  alpha,
  styled,
} from "@mui/material";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  User,
  MessageSquare,
  Send,
} from "lucide-react";
import { useSendClientQueryMutation } from "services/clientApi";
import { useLoading } from "components/LoadingProvider";
import { useSnackbar } from "components/SnackbarProvider";

/* ---------------- STYLED COMPONENTS ---------------- */

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  background: alpha("#ffffff", 0.95),
  backdropFilter: "blur(10px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.05)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const ContactCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.light,
    0.1
  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    background: "#ffffff",
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.text.secondary,
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: "12px 32px",
  borderRadius: 12,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
  },
}));

/* ---------------- MAIN COMPONENT ---------------- */

const ContactSection = ({ contactsRef }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    contactNumber: "",
  });

  const [sendClientQuery] = useSendClientQueryMutation();
  const { startLoading, stopLoading } = useLoading();
  const showSnackbar = useSnackbar();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      startLoading();
      await sendClientQuery(formData).unwrap();
      showSnackbar(
        "Message sent successfully! We'll get back to you soon.",
        "success"
      );
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        contactNumber: "",
      });
    } catch (error) {
      showSnackbar("Unable to send your message. Please try again.", "error");
      console.error("Error:", error);
    } finally {
      stopLoading();
    }
  };

  return (
    <Box
      ref={contactsRef}
      sx={{
        width: "100%",
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)",
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Fade in timeout={800}>
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: "linear-gradient(135deg, #1FACB3 0%, #137E96 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Get In Touch
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 600,
                mx: "auto",
                fontWeight: 400,
              }}
            >
              Have questions or need assistance? Reach out to us and we'll
              respond promptly.
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={4} alignItems="stretch">
          {/* Left Column - Contact Form */}

          {/* Right Column - Location & Info */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3} sx={{ height: "100%" }}>
              {/* Map Section */}
              <Grid item xs={12}>
                <ContactCard elevation={0}>
                  <Box
                    sx={{
                      height: 300,
                      width: "100%",
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "16px 16px 0 0",
                    }}
                  >
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.765710937325!2d77.58349597320867!3d12.922774315950655!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae158a14c4daf1%3A0x6f75ced4e1de87d4!2sATHOME%20GERICARE%20PVT%20LTD!5e0!3m2!1sen!2sin!4v1743926991393!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      title="AtHome Gericare Location"
                    />
                  </Box>
                  <CardContent>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                    >
                      <MapPin color={theme.palette.primary.main} />
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          Visit Our Office
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ATHOME GERICARE PVT LTD
                          <br />
                          152, 2nd Floor, Skanda Towers
                          <br />
                          4T Block East, 11th Main Road
                          <br />
                          Jayanagar, Bengaluru 560041
                          <br />
                          <Box
                            component="span"
                            sx={{ fontStyle: "italic", fontSize: "0.875rem" }}
                          >
                            Landmark: Above Gilma showroom
                          </Box>
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mt: 2 }}
                        >
                          <Clock size={18} style={{ marginRight: 8 }} />
                          <Typography variant="body2">
                            Mon-Fri: 9:00 AM - 6:00 PM
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </ContactCard>
              </Grid>

              {/* Contact Cards */}
              <Grid item xs={12} sm={6}>
                <ContactCard elevation={0}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: alpha(theme.palette.primary.main, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/in.svg"
                          alt="India Flag"
                          sx={{ width: 24, height: 18 }}
                        />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        India Office
                      </Typography>
                    </Box>
                    <Link
                      href="tel:+917019854530"
                      underline="hover"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "text.primary",
                        fontWeight: 500,
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      <Phone size={16} />
                      +91 7019854530
                    </Link>
                  </CardContent>
                </ContactCard>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ContactCard elevation={0}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: alpha(theme.palette.info.main, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src="https://flagcdn.com/us.svg"
                          alt="USA Flag"
                          sx={{ width: 24, height: 18 }}
                        />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        USA Office
                      </Typography>
                    </Box>
                    <Link
                      href="tel:+14083207170"
                      underline="hover"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "text.primary",
                        fontWeight: 500,
                        "&:hover": {
                          color: "info.main",
                        },
                      }}
                    >
                      <Phone size={16} />
                      +1 408-320-7170
                    </Link>
                  </CardContent>
                </ContactCard>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <GlassPaper elevation={0}>
              <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 4,
                    color: "text.primary",
                  }}
                >
                  Send us a Message
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <User
                                size={20}
                                color={theme.palette.primary.main}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Mail
                                size={20}
                                color={theme.palette.primary.main}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Phone Number"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone
                                size={20}
                                color={theme.palette.primary.main}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ alignItems: "flex-start", mt: 1 }}
                            >
                              <MessageSquare
                                size={20}
                                color={theme.palette.primary.main}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <SubmitButton
                        type="submit"
                        variant="contained"
                        endIcon={<Send size={20} />}
                        fullWidth
                      >
                        Send Message
                      </SubmitButton>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </GlassPaper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactSection;
